import { fetchTab } from "@/api/hooks/tab";
import { createCrossSection } from "@/api/hooks/tools/crossSection";
import { setAllToolLocation } from "@/reducer/TabsState";
import Logger from "@mm/logging";
import type {
  CrossSectionSchemaOutput,
  ElementStyle,
  Location,
  LocationInfo,
} from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { DateTime, Duration } from "luxon";
import { useSelector } from "react-redux";
import type { RootState } from ".";

const logger = Logger.fromFilename(__filename);

export type CrossSectionState = {
  [id: number]: CrossSectionSchemaOutput;
};

const initialState: CrossSectionState = {};

const crossSectionSlice = createSlice({
  name: "crossSection",
  initialState: initialState as CrossSectionState,
  reducers: {
    addCrossSection(state, action: PayloadAction<CrossSectionSchemaOutput>) {
      state[action.payload.id] = action.payload;
    },
    discardCrossSection(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const tool = state[id];

      if (!tool) {
        logger.error("Could not find tool with id", id);
        return;
      }
      delete state[id];
    },
    setCrossSectionLocation(state, action: PayloadAction<{ id: number; props: Location }>) {
      const { id, props } = action.payload;

      const crossSection = state[id];
      if (!crossSection) {
        logger.error("Could not find tool with id", id);
        return;
      }

      crossSection.location = props;
    },
    setCrossSectionLocationInfo(state, action: PayloadAction<{ id: number; props: LocationInfo }>) {
      const { id, props } = action.payload;

      const crossSection = state[id];
      if (!crossSection) {
        logger.error("Could not find tool with id", id);
        return;
      }

      crossSection.location_info = props;
    },
    setCrossSectionTitle(state, action: PayloadAction<{ id: number; title: string }>) {
      const { id, title } = action.payload;

      const crossSection = state[id];
      if (!crossSection) {
        logger.error("Could not find cross section with id", id);
        return;
      }

      crossSection.title = title;
    },
    setCrossSectionTitleStyleProps(state, action: PayloadAction<{ id: number; props: ElementStyle }>) {
      const { id, props } = action.payload;

      const crossSection = state[id];
      if (!crossSection) {
        logger.error("Could not find cross section with id", id);
        return;
      }

      crossSection.titleStyle = { ...crossSection.titleStyle, ...props };
    },
    setCrossSectionModel(state, action: PayloadAction<{ id: number; model: string }>) {
      const { id, model } = action.payload;

      const crossSection = state[id];
      if (!crossSection) {
        logger.error("Could not find cross section with id", id);
        return;
      }

      crossSection.model = model;
    },
    setCrossSectionDateTimeRange(
      state,
      action: PayloadAction<{
        id: number;
        props: { datetimeRange: [string | null, string | null]; referenceTime: string };
      }>,
    ) {
      const {
        id,
        props: {
          datetimeRange: [from, to],
          referenceTime,
        },
      } = action.payload;

      const crossSection = state[id];
      if (!crossSection) {
        logger.error("Could not find cross section with id", id);
        return;
      }

      if (!crossSection.time_lock) {
        crossSection.datetime_from = null;
        crossSection.datetime_to = null;

        crossSection.datetime_from_offset = from
          ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;
        crossSection.datetime_to_offset = to
          ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;

        return;
      }

      crossSection.datetime_from = from;
      crossSection.datetime_to = to;
    },
    setCrossSectionTimeSettings(
      state,
      action: PayloadAction<{
        id: number;
        timeLock: boolean;
        from: string;
        to: string;
        referenceTime: string;
      }>,
    ) {
      const { id, timeLock, from, to, referenceTime } = action.payload;

      const crossSection = state[id];
      if (!crossSection) {
        logger.error("Could not find cross section with id", id);
        return;
      }

      crossSection.time_lock = timeLock;

      if (!timeLock) {
        crossSection.datetime_from = null;
        crossSection.datetime_from_offset = from
          ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;

        crossSection.datetime_to = null;
        crossSection.datetime_to_offset = to
          ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;

        return;
      }

      crossSection.datetime_from = crossSection.datetime_from_offset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(crossSection.datetime_from_offset)).toISO()
        : DateTime.fromISO(from).toISO();
      crossSection.datetime_from_offset = null;

      crossSection.datetime_to = crossSection.datetime_to_offset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(crossSection.datetime_to_offset)).toISO()
        : DateTime.fromISO(to).toISO();
      crossSection.datetime_to_offset = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTab.fulfilled, (state, action) => {
      const tab = action.payload;
      for (const key in state) {
        delete state[+key];
      }
      for (const crossSection of tab.cross_sections) {
        state[crossSection.id] = crossSection;
      }
    });
    builder.addCase(createCrossSection.fulfilled, (state, action) => {
      const crossSection = action.payload.tool;
      state[crossSection.id] = crossSection;
    });
    builder.addCase(setAllToolLocation, (state, action) => {
      const { lat, lon, label } = action.payload.location;
      for (const key in state) {
        const crossSection = state[+key];
        if (!crossSection) {
          logger.error("Could not find cross section with id", key);
          continue;
        }
        crossSection.location = { lat, lng: lon };
        crossSection.location_info = { coordinates: { lat, lon }, label };
      }
    });
  },
});

const selectCrossSections = (state: RootState) => state.tabGroup.present.crossSections;

const makeSelectCrossSectionsByTabId = createSelector(
  [selectCrossSections, (state: RootState, tabId: number) => tabId],
  (crossSections, tabId) => Object.values(crossSections).filter((crossSection) => crossSection.id_tab === tabId),
);

export function useCrossSections(tabId: number) {
  return useSelector((state: RootState) => makeSelectCrossSectionsByTabId(state, tabId));
}

const makeSelectCrossSectionById = createSelector(
  [selectCrossSections, (state: RootState, id: number) => id],
  (crossSections, id) => crossSections[id],
);

export function useCrossSection(id: number) {
  return useSelector((state: RootState) => makeSelectCrossSectionById(state, id));
}

export const {
  addCrossSection,
  discardCrossSection,
  setCrossSectionLocation,
  setCrossSectionLocationInfo,
  setCrossSectionTitle,
  setCrossSectionTitleStyleProps,
  setCrossSectionModel,
  setCrossSectionDateTimeRange,
  setCrossSectionTimeSettings,
} = crossSectionSlice.actions;

export default crossSectionSlice.reducer;
